// Adaptive polling — the shared replacement for the bare `setInterval` that
// every "is the job done yet?" screen used to run.
//
// The old pattern was the same everywhere: a fixed delay, running forever,
// running just as hard with the tab buried behind a dozen others. This hook
//
//   * climbs a backoff ladder as the job ages — fast while it's fresh, slow
//     once it's clearly a long one,
//   * drops back to the fast rung the moment the polled state actually
//     changes,
//   * stops dead while `document.hidden`, and fetches immediately on the way
//     back, so a focused tab never sits on stale state,
//   * gives up after a ceiling rather than hammering a wedged job until the
//     browser closes.
//
// Shared via `window.usePolling` — source files are isolated esbuild modules,
// so cross-file symbols travel on `window.*`, not imports.

// Backoff rungs in ms. A screen's own fast interval replaces the first rung
// and any rung faster than it is dropped, so a 60s poller never gets "backed
// off" down to 5s.
const POLL_LADDER = [2000, 5000, 15000, 30000];

// Climb one rung for each of these elapsed since the last observed change.
const POLL_STEP_AFTER = 30000;

// Stop polling once the state has held still this long. Every screen keeps a
// manual Refresh, and remounting (or the job changing) restarts the poll.
const POLL_GIVE_UP_AFTER = 15 * 60 * 1000;

// Distinct from any signature a tick could produce, so the first poll always
// counts as a change.
const POLL_UNSET = {};

const pollLadderFrom = (first) => [first, ...POLL_LADDER.filter((ms) => ms > first)];

const pollSignature = (value) => {
  try { return JSON.stringify(value); } catch (_) { return String(value); }
};

// usePolling(tick, opts)
//
//   tick    — () => any | Promise<any>. One poll. Its resolved value is run
//             through `signature` to decide whether anything changed.
//   active  — poll only while true (default true). A false→true flip restarts
//             at the fastest rung.
//   key     — any scalar identifying what's being polled. Changing it restarts
//             the loop from the fastest rung (and refetches, if `immediate`),
//             the way swapping the record under a plain effect used to.
//   first   — this screen's fast interval, the ladder's first rung.
//   signature — (result) => comparable. Default is a JSON dump of the result.
//             Job screens should return the *status*, not the whole payload:
//             a progress counter that ticks every second would otherwise read
//             as a state change and pin the ladder to its fastest rung.
//   immediate — fire one poll as soon as the hook activates (default false;
//             most callers already load once on mount).
//   giveUpAfter — ms of unchanged, *foreground* polling before stopping.
//             Pass 0 (or null) to poll for as long as the screen is open.
//   onGiveUp — called once when that ceiling is hit.
const usePolling = (tick, opts) => {
  const {
    active = true,
    key = null,
    first = POLL_LADDER[0],
    stepAfter = POLL_STEP_AFTER,
    giveUpAfter = POLL_GIVE_UP_AFTER,
    immediate = false,
    signature,
    onGiveUp,
  } = opts || {};

  // Callers pass fresh closures every render; hold the latest in refs so a new
  // identity never restarts the timer mid-job.
  const tickRef = React.useRef(tick);
  tickRef.current = tick;
  const sigRef = React.useRef(signature);
  sigRef.current = signature;
  const giveUpRef = React.useRef(onGiveUp);
  giveUpRef.current = onGiveUp;

  React.useEffect(() => {
    if (!active) return undefined;
    const ladder = pollLadderFrom(first);
    let alive = true;
    let stopped = false;
    let timer = null;
    let lastChangeAt = Date.now();
    let lastSig = POLL_UNSET;
    let hiddenAt = null;
    let inFlight = false;

    const nextDelay = () => {
      const rung = Math.floor((Date.now() - lastChangeAt) / stepAfter);
      return ladder[Math.min(rung, ladder.length - 1)];
    };

    const schedule = () => {
      if (!alive || stopped || document.hidden) return;
      timer = setTimeout(run, nextDelay());
    };

    const run = async () => {
      timer = null;
      // `inFlight` keeps a visibilitychange from firing a second fetch on top
      // of one that's still in the air.
      if (!alive || stopped || inFlight || document.hidden) return;
      let result;
      inFlight = true;
      try {
        result = await tickRef.current();
      } catch (_) {
        // A failed poll isn't a state change — let the ladder keep climbing.
        inFlight = false;
        if (alive) schedule();
        return;
      }
      inFlight = false;
      if (!alive || stopped) return;
      const sig = sigRef.current ? sigRef.current(result) : pollSignature(result);
      if (sig !== lastSig) {
        lastSig = sig;
        lastChangeAt = Date.now();
      } else if (giveUpAfter && Date.now() - lastChangeAt >= giveUpAfter) {
        stopped = true;
        if (giveUpRef.current) giveUpRef.current();
        return;
      }
      schedule();
    };

    const onVisibility = () => {
      if (timer) { clearTimeout(timer); timer = null; }
      if (document.hidden) { hiddenAt = Date.now(); return; }
      // Background time is not the job standing still in front of the user —
      // discount it from both the ladder and the give-up ceiling, then fetch
      // right now so a job that finished off-screen reads as finished.
      if (hiddenAt) { lastChangeAt += Date.now() - hiddenAt; hiddenAt = null; }
      run();
    };
    document.addEventListener('visibilitychange', onVisibility);

    if (immediate) run(); else schedule();

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active, key, first, stepAfter, giveUpAfter, immediate]);
};

window.usePolling = usePolling;
